import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Radio } from "lucide-react";

/**
 * LiveScoreTicker — horizontal strip below the top nav on `/`.
 *
 * Shows:
 *   • A pulsing "LIVE · SEED n · Team A vs Team B" chip whenever any match is
 *     in progress.
 *   • The last 3 finalised match scores (Seed n · A n — n B).
 *
 * Every team name links to `/standings?highlight=<team_id>` — the standings
 * page auto-scrolls to and briefly glows that row (iter79 · Season Standings
 * Highlight).
 *
 * Refresh strategy: initial fetch + 30s poll + SSE `match.finalized`/
 * `match.set_scored` listeners.
 */
const SEED_ACCENT = { 1: "#F4B400", 2: "#00A86B", 3: "#7DD3FC" };

export default function LiveScoreTicker() {
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    let alive = true;
    let es = null;
    const fetchMatches = () =>
      api
        .get("/public/matches")
        .then(({ data }) => alive && setMatches(data || []))
        .catch(() => {});
    fetchMatches();
    const iv = setInterval(fetchMatches, 30_000);
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      es = new EventSource(`${base}/api/public/events/stream`);
      es.addEventListener("match.finalized", fetchMatches);
      es.addEventListener("match.set_scored", fetchMatches);
    } catch {
      /* silent */
    }
    return () => {
      alive = false;
      clearInterval(iv);
      try {
        es && es.close();
      } catch {
        /* noop */
      }
    };
  }, []);

  const { live, finished } = useMemo(() => {
    const live = (matches || []).filter((m) => (m.status || "").toLowerCase() === "live");
    const finishedAll = (matches || []).filter(
      (m) => (m.status || "").toLowerCase() === "finished"
    );
    // Most recent finished first — prefer scheduled_at desc as we don't
    // always have a finalised_at.
    finishedAll.sort(
      (a, b) => (b.scheduled_at || "").localeCompare(a.scheduled_at || "")
    );
    return { live, finished: finishedAll.slice(0, 3) };
  }, [matches]);

  if (live.length === 0 && finished.length === 0) return null;

  return (
    <div
      className="border-b border-white/5 bg-[#0a1611]/90 backdrop-blur-md"
      data-testid="home-live-ticker"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center gap-2 md:gap-4 overflow-x-auto py-2 no-scrollbar">
        <div className="shrink-0 text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-500 mr-1">
          Ticker
        </div>

        {live.map((m) => {
          const accent = SEED_ACCENT[m.seed] || "#F4B400";
          return (
            <div
              key={`live-${m.id}`}
              className="shrink-0 flex items-center gap-2 pl-2 pr-3 py-1 rounded-full bg-red-500/10 border border-red-500/40"
              data-testid={`ticker-live-${m.id}`}
            >
              <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-red-300">
                <Radio className="w-3 h-3 animate-pulse" /> Live
              </span>
              {m.seed ? (
                <span
                  className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
                  style={{ background: `${accent}22`, color: accent }}
                >
                  Seed {m.seed}
                </span>
              ) : null}
              <TeamLink id={m.team_a_id} name={m.team_a_name} />
              <span className="font-anton tabular-nums text-sm text-white">
                {m.total_games_won_a ?? m.score_a ?? 0}
                <span className="text-zinc-500 mx-1">—</span>
                {m.total_games_won_b ?? m.score_b ?? 0}
              </span>
              <TeamLink id={m.team_b_id} name={m.team_b_name} />
            </div>
          );
        })}

        {finished.map((m) => {
          const accent = SEED_ACCENT[m.seed] || "#94a3b8";
          const aWon = (m.total_games_won_a ?? m.score_a ?? 0) > (m.total_games_won_b ?? m.score_b ?? 0);
          return (
            <div
              key={`fin-${m.id}`}
              className="shrink-0 flex items-center gap-2 px-3 py-1 rounded-full bg-black/40 border border-white/10"
              data-testid={`ticker-finished-${m.id}`}
            >
              {m.seed ? (
                <span
                  className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
                  style={{ background: `${accent}22`, color: accent }}
                >
                  S{m.seed}
                </span>
              ) : null}
              <TeamLink id={m.team_a_id} name={m.team_a_name} winner={aWon} />
              <span className="font-anton tabular-nums text-sm text-zinc-300">
                {m.total_games_won_a ?? m.score_a ?? 0}
                <span className="text-zinc-600 mx-1">—</span>
                {m.total_games_won_b ?? m.score_b ?? 0}
              </span>
              <TeamLink id={m.team_b_id} name={m.team_b_name} winner={!aWon} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeamLink({ id, name, winner }) {
  if (!id) return <span className="text-xs text-zinc-500">TBD</span>;
  return (
    <Link
      to={`/standings?highlight=${id}`}
      className={`text-xs font-bold hover:text-[#F4B400] transition-colors truncate max-w-[9rem] ${
        winner ? "text-white" : "text-zinc-300"
      }`}
      data-testid={`ticker-team-${id}`}
      title={`View ${name || "team"} in standings`}
    >
      {name || "TBD"}
    </Link>
  );
}
